import { createColumnHelper, type ColumnDef } from '@tanstack/react-table';
import { ChevronDown, ChevronRight, Eye, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MrsLink, MRS_CHIP_SHAPE } from '@/components/MrsLink';
import { cn, titleIfClamped } from '@/lib/utils';
import { MrsChip } from './mrs-hover-card';
import type { PicklistTableRow } from '../types';
import {
  aggregate,
  segments,
  pickState,
  postState,
  itemState,
  barTooltip,
  progressFigures,
  rowMeta,
  itemQuantityLine,
  firstLinePerMrs,
} from '../utils/picklistProgress';
import { StatusPill, ProgressBar, ProgressDots } from './PicklistProgressBits';

/**
 * PW-12 — the picklist landing's columns.
 *
 * ## What changed, and why it is not a restyle
 *
 * **Progress is quantity-weighted.** Every figure here comes from `picklistProgress`, which sums the
 * quantities. The four readers of the server's `progressPercentage` are gone: it counts LINES whose
 * `IsPicked` flag is set, so a line picked 190 of 240 contributed zero — the bar under-reported real work
 * on exactly the picklists where progress mattered most.
 *
 * **The status cell carries TWO pills, not one.** Picking and posting are separate journeys and a picklist
 * is not done until both finish. One `PicklistStatus` badge could not say "fully picked, nothing posted",
 * which is the state most likely to need someone's attention.
 *
 * **`documentStatus` is rendered.** It was on every row and shown nowhere, so a CANCELLED picklist looked
 * identical to a closed one.
 *
 * ## Sorting: three keys, and Progress is deliberately not one of them
 *
 * `identifier`, `status` and `createdBy` keep their sorts — they are real SQL columns and the URL persists
 * them. The old `quantity` sort is retired rather than rebuilt: on the Closed tab `sortBy=quantity` silently
 * degraded to `CreatedAt` (the server whitelist has no such key and its fallback arm is `p.CreatedAt`), so
 * the arrow flipped and nothing moved. Quantity-weighted progress is a derived figure, not a column, and it
 * cannot be server-paged — so a sort control on it would reproduce the same lie one tab over.
 */

interface ColumnOptions {
  onNavigateToDetail: (picklistId: number) => void;
}

// ⚠️ EVERY column declares a `size`. The rows render through `DataTableRow`, which stamps
// `cell.column.getSize()` onto each cell — and TanStack's default for a column that does not say
// otherwise is 150, so an omission here does not fall back to "natural width", it silently pins the
// column to 150px. These follow the design grid's proportions.
const columnHelper = createColumnHelper<PicklistTableRow>();

function SortableHeader({ label, column }: { label: string; column: { getIsSorted: () => false | 'asc' | 'desc'; getToggleSortingHandler: () => ((event: unknown) => void) | undefined } }) {
  const sorted = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />
      )}
    </Button>
  );
}

// "qty-weighted" described the PERCENTAGE, and the percentage is no longer in this column — it moved to
// the "Picking · N%" status pill. A qualifier for a number that does not render is worse than no
// qualifier: it sends the reader looking for something that is not there. The column shows a bar and
// three quantities now, so the help says what those are.
const PROGRESS_HELP =
  'The bar is this picklist\'s quantity split by state, and the figures beneath it are those same '
  + 'quantities in the same order: green posted to NetSuite, blue picked and awaiting posting, '
  + 'grey still to pick. These are quantities, not counts of lines.';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createPicklistColumns(options: ColumnOptions): ColumnDef<PicklistTableRow, any>[] {
  return [
    columnHelper.display({
      id: 'expand',
      header: '',
      size: 32,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.rowType !== 'picklist') return null;
        if (!row.getCanExpand()) return null;

        // Decorative: the whole row is the click target and carries aria-expanded, so a second focusable
        // control here would put an unlabelled tab stop in front of every row.
        return (
          <div className="flex h-6 w-6 items-center justify-center text-muted-foreground" aria-hidden>
            {row.getIsExpanded() ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
        );
      },
    }),

    columnHelper.accessor(
      (row) => (row.rowType === 'picklist' ? row.picklist.picklistNumber : ''),
      {
        id: 'identifier',
        size: 220,
        header: ({ column }) => <SortableHeader label="Picklist / Item" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType === 'picklist') {
            const agg = aggregate(row.original.picklist);
            return (
              <div className="min-w-0">
                <span className="font-mono font-medium">{row.original.picklist.picklistNumber}</span>
                <p className="text-xs text-muted-foreground">{rowMeta(agg)}</p>
              </div>
            );
          }
          const { item } = row.original;
          // PT-7 D2 — two lines, then an ellipsis, and the full "{code} · {name}" as a native title only
          // when clamped, measured on pointer enter. It was one truncated line with no hover text at all.
          return (
            <div className="min-w-0 pl-6">
              <span className="font-mono text-sm">{item.itemCode}</span>
              <p
                className="line-clamp-2 text-xs text-muted-foreground"
                onPointerEnter={(e) => titleIfClamped(e.currentTarget, `${item.itemCode} · ${item.itemName}`)}
              >
                {item.itemName}
              </p>
            </div>
          );
        },
      },
    ),

    columnHelper.display({
      id: 'mrs',
      header: 'MRS',
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.rowType === 'picklist') {
          const { picklist } = row.original;
          // PT-7 D1 — a chip stands for an MRS number and takes its id, requestor and department from the
          // first planned line with that number (groups by anchor, then planned-line order).
          const chipLine = new Map(
            firstLinePerMrs((picklist.itemGroups ?? []).flatMap((g) => g.plannedLines ?? [])).map(
              (line) => [line.mrsNumber, line] as const,
            ),
          );
          const visibleMrs = picklist.mrsNumbers.slice(0, 2);
          const overflow = picklist.mrsNumbers.slice(2);
          return (
            // The chip IS the pill (owner ruling 2026-09-22). This cell used to wrap the underlined
            // `link` variant in an outline Badge, so every MRS carried a border AND an underline AND an
            // external-link icon — three pieces of chrome for one number, twice per row, in a 120px
            // column. One chip says the same thing.
            // A chip group: two chips stack in this narrow column, and the card opens below both (CHIP_GROUP_ATTR).
            <div className="flex flex-wrap gap-1" data-mrs-chip-group="">
              {visibleMrs.map((mrs) => {
                const line = chipLine.get(mrs);
                return line !== undefined ? (
                  <MrsChip
                    key={mrs}
                    picklistId={picklist.id}
                    picklistCreatedAt={picklist.createdAt}
                    materialRequestId={line.materialRequestId}
                    mrsNumber={mrs}
                    requestorFullName={line.requestorFullName}
                    departmentName={line.departmentName}
                  />
                ) : (
                  <span key={mrs} className={cn(MRS_CHIP_SHAPE, 'text-muted-foreground')}>
                    {mrs}
                  </span>
                );
              })}
              {overflow.length > 0 && (
                // The overflow chip names what it hides. "+3" alone forces a row expansion to answer
                // "is my MRS in there?".
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className={cn(MRS_CHIP_SHAPE, 'cursor-help text-muted-foreground')}
                    >
                      +{overflow.length}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{overflow.join(', ')}</TooltipContent>
                </Tooltip>
              )}
            </div>
          );
        }
        // PT-7 D2 — the sub-row names its item's FIRST planned line's MRS. It stays a plain link and opens no
        // card: the parent row's chips carry the card.
        const first = row.original.item.plannedLines?.[0];
        return (
          <div className="pl-6">
            {first && <MrsLink mrsId={first.materialRequestId} mrsNumber={first.mrsNumber} variant="chip" />}
          </div>
        );
      },
    }),

    columnHelper.display({
      id: 'progress',
      size: 260,
      enableSorting: false,
      header: () => (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="cursor-help underline decoration-dotted underline-offset-4"
              aria-label={`Progress. ${PROGRESS_HELP}`}
            >
              Progress
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">{PROGRESS_HELP}</TooltipContent>
        </Tooltip>
      ),
      cell: ({ row }) => {
        if (row.original.rowType === 'picklist') {
          const agg = aggregate(row.original.picklist);
          const segs = segments(agg);
          // Just the bar and its segments as numbers. Both the percentage (top-right) and the headline
          // ("2 / 2 PIECE picked") are gone: with the dots reporting every state the cell was carrying
          // three summaries of one partition.
          //
          // ⚠️ The headline was the ONLY place the unit of measure appeared on this row. It now lives in
          // the bar's tooltip and on the detail page. If a unit turns out to be needed here, put it once
          // beside the dot row — never on each dot, which is the compactness this change bought.
          //
          // `agg.percent` is still live: it labels the "Picking · N%" status pill one column over.
          return (
            <div className="min-w-44 space-y-1.5">
              <ProgressBar segs={segs} tooltip={barTooltip(agg)} />
              <ProgressDots figures={progressFigures(agg)} tooltip={barTooltip(agg)} />
            </div>
          );
        }
        const { item } = row.original;
        const itemAgg = {
          toPick: item.quantityToPick,
          picked: item.quantityPicked,
          posted: item.quantityPosted,
          inflight: item.postingQuantity,
        };
        // The item line gets the parent's treatment, from the same `progressFigures` — one partition
        // described one way at both levels. It used to read "2 posted · 2 picked / 2 PAIR", which named
        // the same middle bucket "picked" that the parent row stopped calling that, and mixed a
        // cumulative figure with a target in one string.
        //
        // `max-w-56` is gone: 224px fits "2 posted · 2 picked / 2 PAIR" but not four labelled figures,
        // and the constraint existed to stop a bar spanning the column, not to clip text.
        //
        // The TOOLTIP stays `itemQuantityLine` on purpose — an item line has exactly one unit of measure,
        // so it is the one place the hover can still say "8 picked / 10 pcs" without ambiguity.
        return (
          // pl-3, not pl-6. The indent costs width the dot row needs, and at pl-6 an item whose figures
          // ran to two digits ("17 to pick") wrapped to two lines while its siblings stayed on one — so
          // rows in the same breakdown rendered at different heights purely because of a number's width.
          // The row is already marked as a child by its left border and muted background; 12px of indent
          // says the same thing as 24px and leaves room for the text.
          <div className="space-y-1 pl-3">
            {/* 6px against the parent's 8px — the child is deliberately lighter (owner, 2026-09-11).
                What was actually wrong before was never parent-vs-child: it was that `8DI-LAM-18W270-S`
                rendered a 34px dot row beside siblings at 16px, because "17 to pick" is wider than "0 to
                pick" and tipped past the indented width. SIBLINGS must match each other; a child may
                differ from its parent. Measured across 94 item rows under 20 parents: every bar 6px, every
                dot row 16px. */}
            <ProgressBar segs={segments(itemAgg)} tooltip={itemQuantityLine(item)} size="sm" />
            <ProgressDots figures={progressFigures(itemAgg)} tooltip={itemQuantityLine(item)} />
          </div>
        );
      },
    }),

    columnHelper.accessor(
      (row) => (row.rowType === 'picklist' ? row.picklist.picklistStatus : -1),
      {
        id: 'status',
        size: 150,
        header: ({ column }) => <SortableHeader label="Status" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType === 'picklist') {
            const { picklist } = row.original;
            const agg = aggregate(picklist);
            // Two pills, always both: a picklist is not finished until its picked stock has reached
            // NetSuite, and one badge could never say "picked, nothing posted".
            // PT-7 C3 — a third only when a serial allocated here was picked on another picklist. It comes
            // from the next list read (no live push); the Closed tab's reads carry no bins, so never there.
            const taken = (picklist.itemGroups ?? []).reduce(
              (n, g) => n + (g.bins ?? []).filter((b) => b.takenBy).length,
              0,
            );
            return (
              <div className="flex flex-col items-start gap-1">
                <StatusPill state={pickState(picklist, agg)} />
                <StatusPill state={postState(picklist, agg)} />
                {taken > 0 && (
                  <StatusPill
                    className="max-w-full"
                    state={{
                      tone: 'destructive',
                      label: taken === 1 ? '1 serial needs a swap' : `${taken} serials need a swap`,
                      tooltip:
                        'A serial allocated on this picklist was picked on another picklist. Swap it from the '
                        + 'item’s bin card, or on the handheld.',
                    }}
                  />
                )}
              </div>
            );
          }
          return (
            <div className="pl-6">
              <StatusPill state={itemState(row.original.item)} />
            </div>
          );
        },
      },
    ),

    columnHelper.accessor(
      (row) => (row.rowType === 'picklist' ? row.picklist.createdByName : ''),
      {
        id: 'createdBy',
        size: 130,
        header: ({ column }) => <SortableHeader label="Created by" column={column} />,
        cell: ({ row }) => {
          if (row.original.rowType !== 'picklist') return null;
          const { picklist } = row.original;
          return (
            <div>
              <span className="text-sm">{picklist.createdByName}</span>
              <p className="text-xs text-muted-foreground">
                {new Date(picklist.createdAt).toLocaleDateString()}
              </p>
            </div>
          );
        },
      },
    ),

    columnHelper.display({
      id: 'actions',
      header: '',
      // ONE control. The Edit pencil navigated to the SAME detail route as the eye — two buttons, one
      // destination — so it was never a second capability, and the design's 44px actions column has room
      // for exactly the one that says what it does.
      size: 56,
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.rowType !== 'picklist') return null;
        const { picklist } = row.original;

        return (
          <div className="flex gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`View picklist ${picklist.picklistNumber}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    options.onNavigateToDetail(picklist.id);
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>View details</TooltipContent>
            </Tooltip>
          </div>
        );
      },
    }),
  ];
}